import React, { Component } from "react"
import loadable from "../../Layout/loadable"
import FooterComponent from "./../../Generic/Footer/FooterComponent"
import HeaderComponent from "./../../Generic/Header/HeaderComponent"
import * as endpoint from "./../../../Service/endpoints"
import * as service from "./../../../Service/service"
import { connect } from "react-redux"
import ServiceAreaSelect from "../Homepage/ServiceArea/ServiceAreaSelect"
import SeoMeta from "./../../Generic/SEO/SeoMeta"
import Breadcrumb from "../../UI/Breadcrumb"
import ProductGrid from "../../UI/ProductGrid"
import Icon from "../../UI/Icon"
import { Badge, Button, EmptyState, LoadError, SectionHeader, Skeleton, isPrerender } from "../../UI/Primitives"
import ProductGallery from "./ProductGallery"
import RentalPanel from "./RentalPanel"
import { refreshCartCount } from "../../../Service/cartCount"
import { availability, exGst, formatINR, getProductsByCategory } from "../../../Service/catalog"
import { getCategoryMeta } from "../Catalog/categoryMeta"
import "./product.css"

// Campaign-only form: loaded on demand for ?medium= visitors.
const ResponseForm = loadable(() => import(/* webpackChunkName: "campaign" */ "../Homepage/GoogleForm/ResponseForm"))

const PRODUCT_FAQ = [
  {
    question: "What is this security deposit?",
    description:
      "Voorent requires you to pay a refundable security deposit of about 1 months rent at the time of booking your order. This amount will be refunded to you when you terminate your subscription.",
  },
  {
    question: "When will you return my security deposit?",
    description:
      "Security deposit will be refunded after you terminate the subscription and we pick up all the furniture from your home.",
  },
  {
    question:
      "Is there a minimum rental tenure? What happens if I stop my subscription before completing this minimum tenure?",
    description:
      "In case there is an early termination of the order as placed by the User, the User can request for early closure by informing the Company 2 weeks prior to the selected closure date. In such a case where the User wishes to terminate the order earlier than the prescribed time, the Company shall not refund any security deposit to the User at any point owing to early termination of the agreement (security deposit will be forfeited).",
  },
]

class ProductComponent extends Component {
  state = {
    product: {
      featureList: [],
      gallery: {
        images: [],
      },
    },
    productSlug: "",
    inventory: {
      rentOption: [],
    },
    durationSelected: 12,
    quantity: 1,
    showSpinner: false,
    showOptions: false,
    busy: null,
    notice: null,
    shareLabel: "Share",
    recommendItem: [],
    isLoaded: false,
    loadError: false,
    openResponseForm: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      ...this.state,
      productSlug: props.match.params.productSlug,
    }
  }

  componentDidMount() {
    this.getProductData()
    // Campaign visitors (?medium=…) see the response form after 5 seconds.
    if (window.location.href.indexOf("medium") > -1) {
      this.responseTimer = setTimeout(() => this.setState({ openResponseForm: true }), 5000)
    }
  }

  // The mobile sticky CTA replaces the bottom navigation while it is shown.
  componentDidUpdate() {
    document.body.classList.toggle("v-pdp-page", this.hasStickyCta())
  }

  hasStickyCta = () => {
    const { product, inventory } = this.state
    return !!(product.id && inventory.rentOption && inventory.rentOption.length > 0 && product.isMedical !== true)
  }

  componentWillUnmount() {
    document.body.classList.remove("v-pdp-page")
    clearTimeout(this.responseTimer)
    clearTimeout(this.shareTimer)
    this.unmounted = true
  }

  setNotice = (text, tone = "info") => {
    this.setState({ notice: text ? { text, tone } : null })
  }

  manageQuantity = (op) => {
    if (op === "+") {
      if (this.state.quantity < this.state.product.inventory) {
        this.setState({
          quantity: Number.parseInt(this.state.quantity) + 1,
          notice: null,
        })
      } else {
        this.setNotice(`You can only add up to ${this.state.product.inventory} items.`)
      }
    } else if (op === "-") {
      if (this.state.quantity !== 1) {
        this.setState({
          quantity: Number.parseInt(this.state.quantity) - 1,
          notice: null,
        })
      }
    }
  }

  getProductData = () => {
    this.setState({ isLoaded: false, loadError: false })
    service.getSingleProduct(this.state.productSlug).then(
      (response) => {
        if (this.unmounted) return
        const product = response.data
        if (!product || !product.id) {
          this.setState({ isLoaded: true, product: { featureList: [], gallery: { images: [] } } })
          return
        }
        this.setState({
          product: {
            ...product,
            featureList: product.featureList || [],
            gallery: product.gallery || { images: [] },
          },
          isLoaded: true,
        })
        if (product.isMedical) {
          this.setState({ durationSelected: 1 })
        }
        this.checkProductInventory(product.id)
        this.getRecommendedItem(product)
      },
      () => {
        if (!this.unmounted) this.setState({ isLoaded: true, loadError: true })
      },
    )
  }

  checkProductInventory = (productId) => {
    service.checkInventory(productId).then(
      (response) => {
        if (!this.unmounted) this.setState({ inventory: response.data || { rentOption: [] } })
      },
      () => {
        if (!this.unmounted) this.setNotice("We couldn't load live pricing right now. Please refresh to try again.", "error")
      },
    )
  }

  selectDuration = (value) => {
    const period = value && value.target ? value.target.value : value
    this.setState({
      durationSelected: Number.parseInt(period),
    })
  }

  showPrice = (type) => {
    if (type === "single") {
      let price = "0"
      this.state.inventory.rentOption.forEach((rent) => {
        if (Number(rent.period) === Number(this.state.durationSelected)) {
          price = rent.price
        }
      })
      return price
    } else if (type === "final") {
      let price = "0"
      this.state.inventory.rentOption.forEach((rent) => {
        if (Number(rent.period) === Number(this.state.durationSelected)) {
          price = rent.price
        }
      })
      return price * this.state.quantity
    }
  }

  // intent: "cart" keeps the shopper on the page, "rent" continues to the cart.
  addItemToCart = (intent = "cart") => {
    if (this.state.product.instantDelivery && this.state.quantity > this.state.product.inventory) {
      this.setNotice(`You can only add up to ${this.state.product.inventory} items.`)
      return
    }

    if (this.props.userState.loggedIn) {
      this.setState({
        showSpinner: true,
        busy: intent,
        notice: null,
      })

      service
        .checkCart()
        .then((response) => {
          const cartItems = response.data.cartProductList || []

          if (cartItems.length > 0) {
            if (cartItems[0].isPremium && this.state.product.category.slug !== "premium") {
              this.setState({ showSpinner: false, busy: null })
              this.setNotice("Please add only premium products or remove premium products from the cart to continue.")
              return
            } else if (!cartItems[0].isPremium && this.state.product.category.slug === "premium") {
              this.setState({ showSpinner: false, busy: null })
              this.setNotice("Please add only non-premium products or remove non-premium products from the cart to continue.")
              return
            } else {
              this.updateCart(intent)
            }
          } else {
            this.updateCart(intent)
          }
        })
        .catch(() => {
          this.setState({ showSpinner: false, busy: null })
          this.setNotice("We couldn't reach your cart. Please check your connection and try again.", "error")
        })
    } else {
      this.props.showLoginWindow()
    }
  }

  updateCart = (intent) => {
    const cart = {
      productId: this.state.product.id,
      productSlug: this.state.product.slug,
      tenure: this.state.product.isMedical === true ? 1 : this.state.durationSelected,
      quantity: this.state.quantity,
      monthlyRent: this.showPrice("final"),
      securityDeposit:
        this.state.product.category.slug === "seasonal" ? 0 : Number.parseFloat(this.showPrice("final")) * 1,
      isMedical: this.state.product.isMedical === true ? true : false,
      isPremium: this.state.product.category.slug === "premium" ? true : false,
      isSeasonal: this.state.product.category.slug === "seasonal" ? true : false,
      isExpress: this.state.product.instantDelivery,
    }

    service.addToCart(cart).then(
      () => {
        refreshCartCount()
        if (intent === "rent") {
          window.location.href = endpoint.getRoot("cart")
          return
        }
        this.setState({
          showSpinner: false,
          showOptions: true,
          busy: null,
        })
      },
      () => {
        this.setState({ showSpinner: false, busy: null })
        this.setNotice("We couldn't add this to your cart. Please try again in a moment.", "error")
      },
    )
  }

  getRecommendedItem = (product) => {
    service.recommendedItems(product.id).then(
      (response) => {
        const items = Array.isArray(response.data) ? response.data : []
        if (items.length) {
          this.setState({ recommendItem: items })
        } else {
          this.getSimilarItems(product)
        }
      },
      () => this.getSimilarItems(product),
    )
  }

  // Fallback when the recommendation API has nothing: same sub-category first.
  getSimilarItems = (product) => {
    if (!product.category) return
    getProductsByCategory(product.category.slug).then(
      (list) => {
        if (this.unmounted) return
        const others = list.filter((p) => p.id !== product.id)
        const sameSub = others.filter((p) => p.subCategory && product.subCategory && p.subCategory.slug === product.subCategory.slug)
        const rest = others.filter((p) => sameSub.indexOf(p) === -1)
        this.setState({ recommendItem: [...sameSub, ...rest].slice(0, 4) })
      },
      () => {},
    )
  }

  getExpressDeliverySlotAndCountdown = () => {
    const now = new Date()
    const deliveryDate = new Date(now)
    let label = ""
    let cutoff = new Date()

    if (now.getHours() >= 12) {
      // After 12 PM => next day delivery; order window runs till midnight.
      deliveryDate.setDate(now.getDate() + 1)
      label = "Next Day Delivery"
      cutoff.setHours(23, 59, 59, 999)
    } else {
      // Before 12 PM => same day delivery.
      label = "Same Day Delivery"
      cutoff.setHours(12, 0, 0, 0)
    }

    const diffMs = cutoff - now
    let countdown = "0Hr 0m"

    if (diffMs > 0) {
      const totalSeconds = Math.floor(diffMs / 1000)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      countdown = `${hours}Hr ${minutes}m ${seconds}s`
    }

    const options = { day: "numeric", month: "long" }
    const formattedDate = deliveryDate.toLocaleDateString("en-IN", options)

    return {
      label,
      deliverySlot: `${formattedDate} `,
      countdown,
      deliveryDate,
    }
  }

  share = () => {
    const url = window.location.href
    const title = this.state.product.name
    if (navigator.share) {
      navigator.share({ title, url }).catch(() => {})
      return
    }
    const done = () => {
      this.setState({ shareLabel: "Link copied" })
      clearTimeout(this.shareTimer)
      this.shareTimer = setTimeout(() => this.setState({ shareLabel: "Share" }), 2000)
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, () => {})
    }
  }

  renderSkeleton() {
    return (
      <div className="v-pdp__grid" aria-busy="true" aria-label="Loading product">
        <Skeleton className="v-pdp__skeleton-media" radius="var(--radius-xl)" />
        <div>
          <Skeleton width="30%" height="14px" />
          <Skeleton width="80%" height="40px" style={{ marginTop: 16 }} />
          <Skeleton width="95%" height="14px" style={{ marginTop: 20 }} />
          <Skeleton width="88%" height="14px" style={{ marginTop: 10 }} />
          <Skeleton width="100%" height="320px" radius="var(--radius-lg)" style={{ marginTop: 28 }} />
        </div>
      </div>
    )
  }

  render() {
    const { product, isLoaded, loadError } = this.state
    const citySlug = this.props.match?.params?.city || "delhi"
    const currentCity = endpoint.getCurrentCity()
    const cityLabel = currentCity.charAt(0).toUpperCase() + currentCity.slice(1)
    const categorySlug = this.props.match?.params?.category || product.category?.slug || ""
    const subCategorySlug = this.props.match?.params?.subCategory || product.subCategory?.slug || ""
    const productName = product.name || "Product"
    const productDescription = product.description || `Rent ${productName} online with flexible plans from Voorent.`
    const canonical = `https://voorent.com/${citySlug}/${categorySlug}/${subCategorySlug}/${this.state.productSlug}`
    const images = product.id
      ? [product.featuredImage, ...(product.gallery.images || []).map((img) => img.imageURL)].filter(
          (src, index, all) => src && all.indexOf(src) === index,
        )
      : []
    const categoryMeta = getCategoryMeta(categorySlug, product.category && product.category.name)
    const stock = availability(product)
    const isExpress = !!product.instantDelivery && currentCity === "gurgaon"
    const monthly = product.id && product.isMedical !== true ? exGst(this.showPrice("single")) : 0
    const stickyReady = this.hasStickyCta()

    const structuredData = product.id
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: productName,
          description: productDescription,
          image: images,
          sku: product.productId || String(product.id),
          category: product.category ? product.category.name : undefined,
          brand: {
            "@type": "Brand",
            name: "Voorent",
          },
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: exGst(product.startingFrom),
            availability: stock === "out" ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            url: canonical,
          },
        }
      : null

    return (
      <React.Fragment>
        {product.id ? (
          <SeoMeta
            title={`${productName} on Rent | Voorent`}
            description={productDescription}
            canonical={canonical}
            image={product.featuredImage}
            type="product"
            structuredData={structuredData}
          />
        ) : null}

        {window.location.href.indexOf("medium") > -1 && (
          <div>
            {this.state.openResponseForm && <ResponseForm />}
            {!this.props.surveyModal.show && <ServiceAreaSelect />}
          </div>
        )}
        <HeaderComponent />

        <main id="main-content" className="v-main v-pdp">
          <div className="v-container">
            <Breadcrumb
              items={[
                { label: "Home", to: endpoint.getPath("") },
                { label: categoryMeta.short, to: categorySlug ? endpoint.getPath(categorySlug) : null },
                ...(product.subCategory
                  ? [{ label: product.subCategory.name, to: endpoint.getPath(`${categorySlug}/${subCategorySlug}`) }]
                  : []),
                { label: productName, to: null },
              ]}
            />

            {!isLoaded || (loadError && isPrerender()) ? (
              this.renderSkeleton()
            ) : loadError ? (
              <LoadError onRetry={this.getProductData} />
            ) : !product.id ? (
              <EmptyState
                kind="unavailable"
                title="This product is no longer available"
                description="It may have been rented out or retired from the catalogue. Here are some places to keep browsing."
                actions={
                  <>
                    {categorySlug ? (
                      <Button to={endpoint.getPath(categorySlug)} variant="primary">
                        Browse {categoryMeta.short.toLowerCase()}
                      </Button>
                    ) : null}
                    <Button to={endpoint.getPath("")} variant="secondary">
                      Go to homepage
                    </Button>
                  </>
                }
              />
            ) : (
              <>
                <div className="v-pdp__grid">
                  <div className="v-pdp__media">
                    <ProductGallery images={images} name={productName} outOfStock={stock === "out"} />
                  </div>

                  <div className="v-pdp__info">
                    <div className="v-pdp__badges">
                      {product.category ? <Badge tone="brand">{product.category.name}</Badge> : null}
                      {stock === "in" ? (
                        <Badge tone="success" dot>
                          Available
                        </Badge>
                      ) : null}
                      {stock === "out" ? <Badge tone="danger">Out of stock</Badge> : null}
                      {isExpress ? <Badge tone="accent">Express delivery</Badge> : null}
                    </div>
                    <h1 className="v-pdp__title">{productName}</h1>
                    {product.description ? <p className="v-pdp__desc">{product.description}</p> : null}

                    <RentalPanel
                      product={product}
                      inventory={this.state.inventory}
                      durationSelected={this.state.durationSelected}
                      onSelectDuration={this.selectDuration}
                      quantity={this.state.quantity}
                      onQuantity={this.manageQuantity}
                      showPrice={this.showPrice}
                      onRentNow={() => this.addItemToCart("rent")}
                      onAddToCart={() => this.addItemToCart("cart")}
                      busy={this.state.busy}
                      added={this.state.showOptions}
                      notice={this.state.notice}
                      isExpress={isExpress}
                      expressSlot={this.getExpressDeliverySlotAndCountdown}
                      cityLabel={cityLabel}
                      onChangeCity={this.props.selectCityOpen}
                      onShare={this.share}
                      shareLabel={this.state.shareLabel}
                    />
                  </div>
                </div>

                <div className="v-pdp__details">
                  {product.featureList.length ? (
                    <section aria-labelledby="v-specs-title">
                      <h2 id="v-specs-title" className="v-pdp__h2">
                        Product details
                      </h2>
                      <div className="v-specs">
                        {product.featureList.map((spec, sIndex) => (
                          <div key={sIndex} className="v-specs__group">
                            <h3>{spec.title}</h3>
                            <ul>
                              {(spec.list || []).map((elem, eIndex) => (
                                <li key={eIndex}>{elem.value}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  <section aria-labelledby="v-pfaq-title">
                    <h2 id="v-pfaq-title" className="v-pdp__h2">
                      Frequently asked questions
                    </h2>
                    <div className="v-faq__list">
                      {PRODUCT_FAQ.map((item) => (
                        <details key={item.question} className="v-faq__item">
                          <summary>
                            {item.question}
                            <Icon name="plus" size={18} className="v-faq__icon" />
                          </summary>
                          <p>{item.description}</p>
                        </details>
                      ))}
                    </div>
                  </section>
                </div>

                {this.state.recommendItem.length ? (
                  <section className="v-section" aria-labelledby="v-related-title">
                    <SectionHeader id="v-related-title" eyebrow="You may also like" title="More to complete your space" />
                    <ProductGrid variant="rail" products={this.state.recommendItem.slice(0, 4)} label="Recommended products" />
                  </section>
                ) : null}
              </>
            )}
          </div>
        </main>

        {stickyReady ? (
          <div className="v-sticky-cta" role="region" aria-label="Rent this product">
            <div className="v-sticky-cta__price">
              <strong>{formatINR(monthly)}</strong>
              <span>
                /{categorySlug === "seasonal" ? "season" : "mo"}
                {categorySlug !== "seasonal" && product.isMedical !== true ? ` · ${this.state.durationSelected} months` : ""}
              </span>
            </div>
            <Button
              variant="secondary"
              className="v-sticky-cta__cart"
              onClick={() => (this.state.showOptions ? (window.location.href = "/cart") : this.addItemToCart("cart"))}
              disabled={stock === "out" || !!this.state.busy}
              aria-label={this.state.showOptions ? "View cart" : "Add to cart"}
              icon="bag"
            />
            <Button variant="primary" onClick={() => this.addItemToCart("rent")} disabled={stock === "out" || !!this.state.busy}>
              {stock === "out" ? "Out of stock" : "Rent now"}
            </Button>
          </div>
        ) : null}

        <FooterComponent />
      </React.Fragment>
    )
  }
}

const mapStateToProps = (currentState) => {
  return {
    userState: currentState.userState,
    surveyModal: currentState.surveyModal,
  }
}

const mapActionsToProps = (dispatch) => {
  return {
    showLoginWindow: () => {
      dispatch({ type: "login.window.show" })
    },
    selectCityOpen: () => {
      dispatch({ type: "user.city.show" })
    },
  }
}

export default connect(mapStateToProps, mapActionsToProps)(ProductComponent)
